/**
 * Read Piece View
 * One Read piece, split into tappable lines. Readers tap a line to see the
 * author's note on it (noted lines are highlighted) and to heart it. In
 * annotate mode the author taps a line to write or edit their note instead.
 * The panel under the text is always rendered so nothing jumps as lines are tapped.
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Heart } from 'lucide-react';
import { useAuth } from '../../context/AuthenticationContext';
import { capture } from '../../utils/analytics';
import { splitIntoLines } from '../../utils/splitIntoLines';
import './ReadPieceView.css';

const NOTE_MAX = 300;
const promptText = (p) => (p ? [p.emotion, p.connector, p.setting].filter(Boolean).join(' ') : '');

const ReadPieceView = ({ piece: initialPiece, annotate = false, onChange }) => {
  const { token } = useAuth();
  const [piece, setPiece] = useState(initialPiece);
  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => setPiece(initialPiece), [initialPiece]);
  const pieceId = initialPiece._id;
  useEffect(() => setSelected(null), [pieceId]);

  const noteFor = (line) => piece.notes.find((n) => n.line === line);

  const select = (line) => {
    const next = line === selected ? null : line;
    setSelected(next);
    setDraft(next ? noteFor(next)?.text || '' : '');
    setMessage('');
  };

  const toggleHeart = async () => {
    const line = selected;
    try {
      const res = await axios.post(`/api/read/${piece._id}/heart`, { line });
      setPiece((p) => ({
        ...p,
        hearts: { ...p.hearts, [line]: res.data.count },
        myHearts: res.data.hearted ? [...p.myHearts, line] : p.myHearts.filter((l) => l !== line)
      }));
      if (res.data.hearted) capture('read_line_hearted', { kind: piece.kind });
    } catch (err) {
      setMessage(err.response?.data?.message || 'Could not save that. Try again.');
    }
  };

  const saveNotes = async (notes) => {
    setSaving(true);
    setMessage('');
    try {
      const res = await axios.put(`/api/read/post/${piece.postId}/notes`, { notes });
      setPiece(res.data);
      if (onChange) onChange(res.data);
      setMessage('Saved.');
      capture('read_notes_saved', { notes: notes.length });
    } catch (err) {
      setMessage(err.response?.data?.message || 'Could not save. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const saveNote = () => saveNotes([
    ...piece.notes.filter((n) => n.line !== selected),
    { line: selected, text: draft.trim() }
  ]);

  const removeNote = () => {
    setDraft('');
    saveNotes(piece.notes.filter((n) => n.line !== selected));
  };

  const onLineKey = (e, line) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      select(line);
    }
  };

  const isClassic = piece.kind === 'classic';
  const note = selected && noteFor(selected);
  const mine = selected && piece.myHearts.includes(selected);

  let hint = 'Tap any line you love to heart it.';
  if (annotate) hint = "Tap a line you're proud of, then say why it works.";
  else if (piece.notes.length) hint = 'Highlighted lines have notes from the author. Tap one to read it, or tap any line you love to heart it.';

  return (
    <article className="read-piece">
      <header className="read-piece-header">
        <span className="read-kicker">{isClassic ? 'A classic' : 'From the community'}</span>
        <h3 className="read-title">{isClassic ? piece.title : promptText(piece.prompt) || 'A WriteFlow piece'}</h3>
        <p className="read-byline">
          by{' '}
          {isClassic
            ? piece.byline
            : <Link to={`/profile/${piece.author._id}`}>{piece.author.fullName || `@${piece.author.username}`}</Link>}
        </p>
      </header>

      <div className="read-text">
        {splitIntoLines(piece.content).map((para, i) => (
          <p key={i}>
            {para.map((line, j) => (
              <React.Fragment key={j}>
                <span
                  role="button"
                  tabIndex={0}
                  aria-pressed={line === selected}
                  className={[
                    'read-line',
                    noteFor(line) ? 'has-note' : '',
                    piece.myHearts.includes(line) ? 'hearted' : '',
                    line === selected ? 'selected' : ''
                  ].filter(Boolean).join(' ')}
                  onClick={() => select(line)}
                  onKeyDown={(e) => onLineKey(e, line)}
                >
                  {line}
                </span>{' '}
              </React.Fragment>
            ))}
          </p>
        ))}
      </div>

      <div className={`read-panel ${selected ? 'active' : ''}`} aria-live="polite">
        {!selected ? (
          <p className="read-panel-hint">{hint}</p>
        ) : annotate ? (
          <>
            <p className="read-panel-quote">{selected}</p>
            <textarea
              className="read-panel-input"
              rows={3}
              maxLength={NOTE_MAX}
              placeholder="Why does this line work? What were you going for?"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
            />
            <div className="read-panel-actions">
              <span className="read-panel-msg">{message || `${draft.length}/${NOTE_MAX}`}</span>
              <button type="button" className="read-btn" onClick={removeNote} disabled={saving || !note}>
                Remove note
              </button>
              <button type="button" className="read-btn read-btn-primary" onClick={saveNote} disabled={saving || !draft.trim()}>
                Save note
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="read-panel-quote">{selected}</p>
            <p className="read-panel-note">
              {note ? note.text : !isClassic && 'No note from the author on this line.'}
            </p>
            <div className="read-panel-actions">
              <span className="read-panel-msg">
                {message || (!token && <Link to="/login">Log in to heart lines</Link>)}
              </span>
              <button
                type="button"
                className={`read-btn read-heart ${mine ? 'on' : ''}`}
                onClick={toggleHeart}
                disabled={!token}
                aria-pressed={!!mine}
                aria-label={mine ? 'Remove heart' : 'Heart this line'}
              >
                <Heart size={16} fill={mine ? 'currentColor' : 'none'} />
                <span>{piece.hearts[selected] || 0}</span>
              </button>
            </div>
          </>
        )}
      </div>

      <footer className="read-footer">
        {isClassic
          ? <span>{piece.credit}</span>
          : !annotate && <Link to={`/post/${piece.postId}`}>Comment on the original post</Link>}
      </footer>
    </article>
  );
};

export default ReadPieceView;
